import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

/** A value that follows `value` after `delay` ms of quiet — for search boxes. */
export function useDebounced<T>(value: T, delay = 200): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * One string of state kept in the URL, so a filtered view survives a reload
 * and can be sent to a colleague. `fallback` is what an absent param means,
 * and setting the fallback removes the param — the URL stays clean.
 */
export function useUrlParam(key: string, fallback = ''): [string, (v: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? fallback;
  const set = useCallback((v: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === fallback || v === '') next.delete(key); else next.set(key, v);
      // A changed filter starts the list over; page 1 is the absent param.
      if (!key.endsWith('page') && !key.endsWith('size')) for (const k of [...next.keys()]) if (k.endsWith('page')) next.delete(k);
      return next;
    }, { replace: true });
  }, [key, fallback, setParams]);
  return [value, set];
}

/** A global key chord (`mod+k`) → handler; `mod` is ⌘ on a Mac and Ctrl elsewhere. */
export function useHotkey(combo: string, handler: (e: KeyboardEvent) => void): void {
  useEffect(() => {
    const parts = combo.toLowerCase().split('+');
    const key = parts[parts.length - 1];
    const needMod = parts.includes('mod');
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== key) return;
      if (needMod !== (e.metaKey || e.ctrlKey)) return;
      handler(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combo, handler]);
}

export type SortDir = 'asc' | 'desc';

export interface SortState { key: string; dir: SortDir }

/** Sort key and direction in the URL as `sort=key.dir` (prefixed when a page holds several tables). */
export function useSort(fallback: SortState, prefix = ''): [SortState, (key: string) => void] {
  const [raw, setRaw] = useUrlParam(`${prefix}sort`, `${fallback.key}.${fallback.dir}`);
  const state = useMemo<SortState>(() => {
    const [key, dir] = raw.split('.');
    return key ? { key, dir: dir === 'desc' ? 'desc' : 'asc' } : fallback;
  }, [raw, fallback]);
  const toggle = useCallback((key: string) => {
    const dir: SortDir = state.key === key && state.dir === 'asc' ? 'desc' : 'asc';
    setRaw(`${key}.${dir}`);
  }, [state, setRaw]);
  return [state, toggle];
}

export type SortValue = string | number | boolean | null | undefined;

/** Sort `rows` by a per-key accessor; nulls sink to the bottom in either direction. */
export function sortRows<T>(rows: T[], sort: SortState, accessors: Record<string, (row: T) => SortValue>): T[] {
  const get = accessors[sort.key];
  if (!get) return rows;
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = get(a), y = get(b);
    if (x === y) return 0;
    if (x === null || x === undefined) return 1;
    if (y === null || y === undefined) return -1;
    if (typeof x === 'string' && typeof y === 'string') return x.localeCompare(y, 'ru') * sign;
    return (x < y ? -1 : 1) * sign;
  });
}

export const PAGE_SIZES = [25, 50, 100] as const;
export const PAGE_SIZE = PAGE_SIZES[0];

/** Page number (1-based) and page size from the URL, the page clamped to what the list has. */
export function usePage(total: number, prefix = ''): { page: number; setPage: (p: number) => void; pages: number; size: number; setSize: (s: number) => void } {
  const [rawPage, setRawPage] = useUrlParam(`${prefix}page`, '1');
  const [rawSize, setRawSize] = useUrlParam(`${prefix}size`, String(PAGE_SIZE));
  const size = (PAGE_SIZES as readonly number[]).includes(Number(rawSize)) ? Number(rawSize) : PAGE_SIZE;
  const pages = Math.max(1, Math.ceil(total / size));
  const page = Math.min(pages, Math.max(1, Number(rawPage) || 1));
  return { page, setPage: (p) => setRawPage(String(p)), pages, size, setSize: (s) => { setRawSize(String(s)); setRawPage('1'); } };
}
